// "Our Reach" — public-facing site reach card.
//
// Numbers are sourced from the Emergent App Analytics dashboard and
// updated manually. To refresh the counters, edit the two constants below
// and redeploy — no backend changes required.

import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Globe, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal } from "./common";

// -----------------------------------------------------------------------
// EDIT THESE VALUES to refresh the "Our Reach" numbers.
// Pull the latest figures from the Emergent dashboard:
//   Emergent → Your app → App Analytics → Last 30 days
// Use plain integers (no commas, no "K" suffix — the counter formats them).
// -----------------------------------------------------------------------
const TOTAL_REQUESTS = 118650; // "Total Requests" on Emergent dashboard
// People engaged is derived from total views (roughly total / 5) so the
// two counters always move in lock-step — no need to hand-update this.
const TOTAL_USERS = Math.round(TOTAL_REQUESTS / 5);

// Country / city view breakdown — also pulled from the Emergent
// dashboard's "Top Countries" / "Top Cities" panels. Each row is
// { flag: <emoji>, name: <string>, value: <integer> }. Sorted highest
// first. Keep the arrays short (4-5 rows) so the list stays compact.
const TOP_COUNTRIES = [
  { flag: "🇨🇦", name: "Canada",        value: 27288 },
  { flag: "🇺🇸", name: "United States", value: 23300 },
  { flag: "🇳🇱", name: "Netherlands",   value: 18967 },
  { flag: "🇧🇷", name: "Brazil",        value: 15750 },
  { flag: "🇫🇷", name: "France",        value: 9295 },
];
const TOP_CITIES = [
  { flag: "🇨🇦", name: "Toronto",       value: 19500 },
  { flag: "🇺🇸", name: "New York",      value: 16400 },
  { flag: "🇳🇱", name: "Amsterdam",     value: 13500 },
  { flag: "🇧🇷", name: "São Paulo",     value: 11100 },
  { flag: "🇫🇷", name: "Paris",         value: 6600 },
];
// -----------------------------------------------------------------------

// Format an integer as compact "K"/"M" — anything under 1,000 stays as
// full digits with commas; ≥1K becomes "9.2K", ≥1M becomes "1.2M".
function formatCompact(n) {
  if (n < 1_000) return n.toLocaleString("en-US");
  if (n < 1_000_000) {
    const v = (n / 1_000).toFixed(1).replace(/\.0$/, "");
    return `${v}K`;
  }
  const v = (n / 1_000_000).toFixed(1).replace(/\.0$/, "");
  return `${v}M`;
}

const METRIC_IDS = [
  { key: "requests", value: TOTAL_REQUESTS, icon: Globe },
  { key: "users",    value: TOTAL_USERS,    icon: Users },
];

// Animate 0 -> `target` when scrolled into view, formatting each frame
// through `formatCompact` so "101.2K" reads naturally as it climbs.
function AnimatedNumber({ target, className = "" }) {
  const [display, setDisplay] = useState(0);
  const started = useRef(false);
  return (
    <motion.span
      className={className}
      onViewportEnter={() => {
        if (started.current || target === 0) return;
        started.current = true;
        const duration = 1600;
        let startTs = null;
        const step = (ts) => {
          if (startTs === null) startTs = ts;
          const p = Math.min((ts - startTs) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(Math.round(eased * target));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }}
      viewport={{ once: true, margin: "-60px" }}
    >
      {formatCompact(display)}
    </motion.span>
  );
}

export default function OurReach() {
  const { t } = useTranslation();
  const METRICS = METRIC_IDS.map((m) => ({
    ...m,
    label: t(m.key === "requests" ? "ourReach.totalViews" : "ourReach.peopleEngaged"),
  }));
  return (
    <section
      className="py-16 sm:py-20 bg-white"
      data-testid="our-reach-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-teal opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-teal" />
            </span>
            <span
              className="text-xs font-semibold uppercase tracking-[0.24em] text-brand-teal"
              data-testid="our-reach-live-badge"
            >
              {t("ourReach.liveBadge")}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold font-heading text-brand-navy text-center max-w-3xl mx-auto leading-tight">
            {t("ourReach.heading")}
          </h2>
          <p className="mt-4 text-center text-base sm:text-lg text-slate-500 max-w-2xl lg:max-w-none mx-auto leading-relaxed lg:whitespace-nowrap">
            {t("ourReach.intro")}
          </p>
        </Reveal>

        <div
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto"
          data-testid="our-reach-metrics"
        >
          {METRICS.map(({ key, label, value, icon: IconEl }, i) => (
            <Reveal key={key} delay={i * 0.08}>
              <motion.div
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                className="relative overflow-hidden rounded-2xl border border-brand-mist bg-gradient-to-br from-white to-brand-offwhite p-5 sm:p-6 shadow-sm hover:shadow-lg hover:border-brand-teal/40 transition-all duration-300"
                data-testid={`our-reach-card-${key}`}
              >
                <div className="relative flex items-center gap-4">
                  <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-brand-mist flex items-center justify-center text-brand-navy shrink-0">
                    <IconEl className="h-6 w-6 sm:h-7 sm:w-7" strokeWidth={2} />
                  </div>
                  <div className="min-w-0">
                    <p
                      className="text-3xl sm:text-4xl font-bold font-heading text-brand-navy tabular-nums leading-none"
                      data-testid={`our-reach-value-${key}`}
                    >
                      <AnimatedNumber target={value} />
                    </p>
                    <p className="mt-1.5 text-xs sm:text-sm font-semibold uppercase tracking-wider text-slate-500">
                      {label}
                    </p>
                  </div>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <TopViewsPanel />
        </Reveal>
      </div>
    </section>
  );
}

// Country / city breakdown block. Toggle pill (like the reference
// screenshot), then a "Traffic over time" chart followed by a leader
// list of flag + name + dotted rule + count.

// Daily traffic pattern (30 days). Normalised — each value scales with
// whichever aggregate total (country vs city) is passed in via `total`
// so switching the tab rescales the chart's Y axis automatically.
const CHART_PATTERN = [
  0.60, 0.95, 1.50, 1.10, 1.72, 1.35, 1.05, 1.55, 1.05, 0.85,
  1.72, 1.15, 1.20, 1.75, 1.00, 0.75, 1.30, 0.95, 0.70, 1.55,
  2.05, 1.05, 0.65, 0.90, 1.15, 1.40, 2.20, 1.60, 0.80, 1.05,
];

function smoothPath(pts) {
  if (pts.length < 2) return "";
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const mx = (x0 + x1) / 2;
    d += ` C ${mx} ${y0}, ${mx} ${y1}, ${x1} ${y1}`;
  }
  return d;
}

function TrafficChart({ total }) {
  const { t } = useTranslation();
  const days = CHART_PATTERN.length;
  const patternSum = CHART_PATTERN.reduce((a, b) => a + b, 0);
  // Scale factor so the sum of the daily values equals `total`.
  const scale = total / patternSum;
  const values = CHART_PATTERN.map((f) => Math.round(f * scale));
  const maxV = Math.max(...values);
  // Round the y-axis up to a nice interval so the top tick lands on a
  // clean number (0, step, 2·step, 3·step, 4·step).
  const roughStep = maxV / 4;
  const pow = Math.pow(10, Math.max(0, Math.floor(Math.log10(roughStep)) - 1));
  const step = Math.ceil(roughStep / (2 * pow)) * (2 * pow);
  const yMax = step * 4;
  const ticks = [0, step, step * 2, step * 3, step * 4];

  const W = 800;
  const H = 260;
  const pad = { l: 52, r: 12, t: 12, b: 32 };
  const cw = W - pad.l - pad.r;
  const ch = H - pad.t - pad.b;
  const xAt = (i) => pad.l + (i * cw) / (days - 1);
  const yAt = (v) => pad.t + ch - (v / yMax) * ch;

  const points = values.map((v, i) => [xAt(i), yAt(v)]);
  const linePath = smoothPath(points);
  const areaPath = `${linePath} L ${xAt(days - 1)} ${pad.t + ch} L ${xAt(0)} ${pad.t + ch} Z`;

  // Build the last 30 rolling dates so the axis feels current.
  const today = new Date();
  const dates = Array.from({ length: days }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (days - 1 - i));
    return d;
  });
  const labelIdxs = [0, Math.floor(days * 0.25), Math.floor(days * 0.5), Math.floor(days * 0.75), days - 1];
  const fmt = (d) => d.toLocaleString(undefined, { month: "short", day: "numeric" });

  return (
    <div className="mb-6" data-testid="our-reach-traffic-chart">
      <p className="text-base sm:text-lg font-semibold font-heading text-brand-navy">
        {t("ourReach.trafficTitle", { defaultValue: "Traffic over time" })}
      </p>
      <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
        {t("ourReach.trafficSubtitle", { defaultValue: "Number of total visits." })}
      </p>
      <div className="mt-4">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Daily traffic chart">
          <defs>
            <linearGradient id="our-reach-area" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%"  stopColor="#6BA292" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#6BA292" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* horizontal gridlines */}
          {ticks.map((v) => (
            <line
              key={`g-${v}`}
              x1={pad.l}
              x2={W - pad.r}
              y1={yAt(v)}
              y2={yAt(v)}
              stroke="#E2E8F0"
              strokeWidth="1"
              strokeDasharray={v === 0 ? "0" : "2 4"}
            />
          ))}
          {/* y-axis labels */}
          {ticks.map((v) => (
            <text
              key={`yl-${v}`}
              x={pad.l - 10}
              y={yAt(v) + 4}
              fill="#94A3B8"
              fontSize="12"
              textAnchor="end"
              fontFamily="'IBM Plex Sans', system-ui, sans-serif"
            >
              {v === 0 ? "0" : formatCompact(v)}
            </text>
          ))}
          {/* area + line */}
          <path d={areaPath} fill="url(#our-reach-area)" />
          <path d={linePath} fill="none" stroke="#4F8274" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
          {/* x-axis date labels */}
          {labelIdxs.map((i, k) => (
            <text
              key={`xl-${i}`}
              x={xAt(i)}
              y={H - 10}
              fill="#94A3B8"
              fontSize="12"
              textAnchor={k === 0 ? "start" : k === labelIdxs.length - 1 ? "end" : "middle"}
              fontFamily="'IBM Plex Sans', system-ui, sans-serif"
            >
              {fmt(dates[i])}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
}

function TopViewsPanel() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("country");
  const rows = tab === "country" ? TOP_COUNTRIES : TOP_CITIES;
  // Chart rescales with the aggregate for the active tab.
  const chartTotal = tab === "country"
    ? TOTAL_REQUESTS
    : TOP_CITIES.reduce((a, r) => a + r.value, 0);

  return (
    <div
      className="mt-8 max-w-4xl mx-auto rounded-2xl border border-brand-mist bg-gradient-to-br from-white to-brand-offwhite p-6 sm:p-8 shadow-sm"
      data-testid="our-reach-top-views"
    >
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          {t("ourReach.topViewsBy")}
        </p>
        {/* Segmented pill toggle */}
        <div
          className="inline-flex items-center rounded-full bg-brand-mist/60 p-1"
          role="tablist"
          data-testid="our-reach-tabs"
        >
          {[
            { key: "country", label: t("ourReach.tabCountry") },
            { key: "city",    label: t("ourReach.tabCity") },
          ].map((tt) => {
            const active = tab === tt.key;
            return (
              <button
                key={tt.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(tt.key)}
                data-testid={`our-reach-tab-${tt.key}`}
                className={`px-4 sm:px-5 py-1.5 text-sm font-semibold rounded-full transition-colors ${
                  active
                    ? "bg-brand-navy text-white shadow-sm"
                    : "text-brand-navy/70 hover:text-brand-navy"
                }`}
              >
                {tt.label}
              </button>
            );
          })}
        </div>
      </div>

      <TrafficChart total={chartTotal} />
      <ul className="divide-y divide-brand-mist/70">
        {rows.map((row) => (
          <li
            key={`${tab}-${row.name}`}
            className="flex items-center gap-3 py-3"
            data-testid={`our-reach-row-${row.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className="text-2xl leading-none" aria-hidden="true">
              {row.flag}
            </span>
            <span className="text-base sm:text-lg font-medium text-brand-navy truncate flex-shrink-0">
              {tab === "country" ? t(`ourReach.countries.${row.name}`, { defaultValue: row.name }) : row.name}
            </span>
            {/* Leader dots that expand to fill the row */}
            <span className="flex-1 min-w-0 mx-2 border-b border-dotted border-brand-mist/80 translate-y-[6px]" />
            <span className="text-base sm:text-lg font-semibold text-brand-navy tabular-nums">
              {formatCompact(row.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
